import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import axios from 'axios';
import api from '../api/api';

const QUARTERS = ['Q1', 'Q2', 'Q3', 'Q4'];

export default function Admin() {
  const [years, setYears]         = useState([]);
  const [year, setYear]           = useState('');
  const [quarter, setQuarter]     = useState('');
  const [operation, setOperation] = useState('U');
  const [file, setFile]           = useState(null);
  const [panFile, setPanFile]     = useState(null);
  const [message, setMessage]     = useState({ text: '', ok: true });
  const [loading, setLoading]     = useState(false);
  const [panLoading, setPanLoading] = useState(false);
  // [LARGE FILE FIX] Track upload progress percentage for large ZIP/RAR files
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileRef    = useRef();
  const panFileRef = useRef();
  const navigate   = useNavigate();
  const { instance } = useMsal();
  const [user] = useState(() => JSON.parse(sessionStorage.getItem('user') || '{}'));

  const handleLogout = () => {
    sessionStorage.clear();
    const isMock = !instance.getActiveAccount();
    if (isMock) { window.location.href = '/'; } else { instance.logoutRedirect({ postLogoutRedirectUri: '/' }); }
  };

  useEffect(() => {
    api.get('/admin/years').then(r => setYears(r.data)).catch(err => console.error('years fetch failed', err?.response?.status));
  }, []);

  const msg = (text, ok = true) => setMessage({ text, ok });
  const reset = () => { setYear(''); setQuarter(''); setFile(null); msg(''); if (fileRef.current) fileRef.current.value = ''; };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!year || !quarter || !file) { msg('Select year, quarter and file', false); return; }
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['zip', 'rar', 'pdf', 'jpg', 'jpeg', 'png', 'xls', 'xlsx', 'doc', 'docx'].includes(ext)) { msg('Only .zip, .rar, .pdf, image, Excel and Word files are allowed', false); return; }
    setLoading(true); msg(''); setUploadProgress(0);
    try {
      // Step 1: init multipart upload
      msg('Preparing upload...');
      let initData;
      try {
        const { data } = await api.post('/admin/upload-init', { finYear: year, quarter, fileName: file.name });
        initData = data;
      } catch {
        msg('Upload could not be started. Please try again.', false);
        return;
      }

      // Step 2: upload file in 10MB chunks with progress
      const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB
      const totalParts = Math.ceil(file.size / CHUNK_SIZE);
      const parts = [];
      const CONCURRENCY = 3;

      msg(`Uploading file... 0%`);
      let completedParts = 0;

      // Upload parts in batches of CONCURRENCY
      for (let i = 0; i < totalParts; i += CONCURRENCY) {
        const batch = [];
        for (let j = i; j < Math.min(i + CONCURRENCY, totalParts); j++) {
          const partNumber = j + 1;
          const start = j * CHUNK_SIZE;
          const chunk = file.slice(start, start + CHUNK_SIZE);
          batch.push(
            api.post('/admin/upload-part-url', { s3Key: initData.s3Key, uploadId: initData.uploadId, partNumber })
              .then(({ data }) => axios.put(data.url, chunk, { headers: { 'Content-Type': '' } }))
              .then((res) => {
                completedParts++;
                const pct = Math.round((completedParts / totalParts) * 100);
                setUploadProgress(pct);
                msg(`Uploading file... ${pct}%`);
                console.log(`[part ${partNumber}] etag:`, res.headers.etag, 'all headers:', JSON.stringify(res.headers));
                return { PartNumber: partNumber, ETag: res.headers.etag };
              })
          );
        }
        const batchResults = await Promise.all(batch);
        parts.push(...batchResults);
      }

      // Step 3: complete multipart upload
      msg('Finalising upload, please wait...');
      try {
        parts.sort((a, b) => a.PartNumber - b.PartNumber);
        await api.post('/admin/upload-complete', { s3Key: initData.s3Key, uploadId: initData.uploadId, parts });
      } catch {
        msg('File upload failed. Please try again.', false);
        return;
      }

      // Step 4: trigger async extraction
      msg('Processing files, please wait...');
      let jobId;
      try {
        const { data } = await api.post('/admin/upload-process', { finYear: year, quarter, s3Key: initData.s3Key });
        jobId = data.jobId;
      } catch {
        msg('Upload succeeded but processing could not be started. Please contact support.', false);
        return;
      }

      // Step 5: poll for extraction progress every 3s
      setUploadProgress(0);
      await new Promise((resolve) => {
        const interval = setInterval(async () => {
          try {
            const { data: job } = await api.get(`/admin/upload-status/${jobId}`);
            if (job.filesTotal > 0) {
              const pct = Math.round((job.filesProcessed / job.filesTotal) * 100);
              setUploadProgress(pct);
              msg(`Processing files... ${job.filesProcessed} of ${job.filesTotal} done`);
            } else {
              msg('Processing files, please wait...');
            }
            if (job.status === 'DONE') {
              clearInterval(interval);
              setUploadProgress(100);
              msg(`All done! ${job.extractedFiles} files uploaded successfully in ${job.processingTime}.`);
              resolve();
            } else if (job.status === 'ERROR') {
              clearInterval(interval);
              msg('Processing failed. Please try again or contact support.', false);
              resolve();
            }
          } catch { /* keep polling */ }
        }, 3000);
      });

    } catch (err) { msg('Something went wrong. Please try again.', false); }
    finally { setLoading(false); setUploadProgress(0); }
  };

  const handlePublish = async () => {
    if (!year || !quarter) { msg('Select year and quarter', false); return; }
    setLoading(true); msg('');
    try {
      const { data } = await api.post('/admin/publish', { finYear: year, quarter });
      msg(data.message || 'Published successfully');
    } catch (err) { msg(err.response?.data?.error || 'Publish failed', false); }
    finally { setLoading(false); }
  };

  const handleDelete = async () => {
    if (!year || !quarter) { msg('Select year and quarter', false); return; }
    if (!window.confirm(`Delete all files for ${year} ${quarter}?`)) return;
    setLoading(true); msg('');
    try {
      const { data } = await api.post('/admin/delete', { finYear: year, quarter });
      msg(data.message || 'Deleted successfully');
    } catch (err) { msg(err.response?.data?.error || 'Delete failed', false); }
    finally { setLoading(false); }
  };

  const handlePanUpload = async (e) => {
    e.preventDefault();
    if (!panFile) { msg('Select a pipe-delimited PAN file', false); return; }
    setPanLoading(true); msg('');
    try {
      const form = new FormData(); form.append('file', panFile);
      const { data } = await api.post('/admin/upload-pan', form);
      if (data.success) {
        msg(`${data.message} — ${data.data?.count ?? 0} records`);
      } else {
        msg(data.error || 'PAN upload completed with unknown status', false);
      }
      setPanFile(null); if (panFileRef.current) panFileRef.current.value = '';
    } catch (err) { msg(err.response?.data?.error || 'PAN upload failed', false); }
    finally { setPanLoading(false); }
  };

  return (
    <div style={s.page}>
      <div style={s.header}>
        <img src="/godrej-logo-dark.svg" alt="Godrej" style={s.logo} />
        <span style={s.headerTitle}>Form 16A — Admin</span>
        <div style={s.headerRight}>
          {user.role === 'SUPERUSER' && <button style={s.linkBtn} onClick={() => navigate('/logs')}>Logs</button>}
          <span style={s.userLabel}>{user.loginName}</span>
          <button className="rounded-pill" style={s.logoutBtn} onClick={handleLogout}>Logout</button>
        </div>
      </div>

      <div style={s.body}>
        <div style={s.card}>
          <div style={s.radioGroup}>
            {[['U','Upload'],['P','Publish'],['D','Delete']].map(([v,l]) => (
              <label key={v} style={s.radioLabel}>
                <input type="radio" value={v} checked={operation===v} onChange={() => { setOperation(v); reset(); }} />
                {' '}{l}
              </label>
            ))}
          </div>

          <div style={s.row}>
            <div style={s.field}>
              <label style={s.label}>Financial Year</label>
              <select style={s.select} value={year} onChange={e => setYear(e.target.value)}>
                <option value="">-- Select --</option>
                {years.map(y => <option key={y.Year} value={y.Year}>{y.Year}</option>)}
              </select>
            </div>
            <div style={s.field}>
              <label style={s.label}>Quarter</label>
              <select style={s.select} value={quarter} onChange={e => setQuarter(e.target.value)} disabled={!year}>
                <option value="">-- Select --</option>
                {QUARTERS.map(q => <option key={q} value={q}>{q}</option>)}
              </select>
            </div>
          </div>

          {operation === 'U' && (
            <form onSubmit={handleUpload}>
              <div style={s.field}>
                <label style={s.label}>File</label>
                <input ref={fileRef} type="file" accept=".zip,.rar,.pdf,.jpg,.jpeg,.png,.xls,.xlsx,.doc,.docx" onChange={e => setFile(e.target.files[0])} style={s.fileInput} />
              </div>
              <button className="rounded-pill" style={s.btn} type="submit" disabled={loading}>{loading ? 'Uploading...' : 'Upload'}</button>
              {loading && uploadProgress > 0 && (
                <div style={s.progressWrap}>
                  <div style={{ ...s.progressBar, width: `${uploadProgress}%` }} />
                  <span style={s.progressLabel}>{uploadProgress < 100 ? `${uploadProgress}%` : 'Done!'}</span>
                </div>
              )}
            </form>
          )}
          {operation === 'P' && (
            <button className="rounded-pill" style={s.btn} onClick={handlePublish} disabled={loading}>{loading ? 'Publishing...' : 'Publish'}</button>
          )}
          {operation === 'D' && (
            <button className="rounded-pill" style={s.btn} onClick={handleDelete} disabled={loading}>{loading ? 'Deleting...' : 'Delete'}</button>
          )}

          {message.text && <p style={{...s.msgBox, background: message.ok ? '#f3e8ef' : '#fdecea', color: message.ok ? 'var(--brand-purple)' : '#c62828'}}>{message.text}</p>}
        </div>

        <div style={s.card}>
          <h3 style={s.sectionTitle}>Bulk PAN Upload</h3>
          <p style={s.hint}>Upload a .txt or .csv file with columns: dealerNum, panNum, name, type (separated by |)</p>
          <form onSubmit={handlePanUpload} style={s.row}>
            <input ref={panFileRef} type="file" accept=".txt,.csv" onChange={e => setPanFile(e.target.files[0])} style={s.fileInput} />
            <button className="rounded-pill" style={s.btn} type="submit" disabled={panLoading}>{panLoading ? 'Uploading...' : 'Upload PAN Data'}</button>
          </form>
        </div>
      </div>
    </div>
  );
}

const s = {
  page:        { minHeight: '100vh', background: '#fafafa' },
  header:      { height: '56px', padding: '0 2rem', display: 'flex', alignItems: 'center', gap: '1rem', borderBottom: '1px solid #F1F1ED', background: '#fff' },
  logo:        { height: '28px' },
  headerTitle: { fontFamily: 'var(--font-headline)', fontSize: '1.1rem', color: '#333', flex: 1 },
  headerRight: { display: 'flex', alignItems: 'center', gap: '12px' },
  userLabel:   { fontSize: '14px', color: 'var(--brand-grey)' },
  logoutBtn:   { background: 'transparent', border: '1px solid var(--brand-purple)', color: 'var(--brand-purple)', padding: '4px 14px', cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: '13px', borderRadius: '50px' },
  linkBtn:     { background: 'transparent', border: 'none', color: 'var(--brand-purple)', cursor: 'pointer', textDecoration: 'underline', fontSize: '14px', fontFamily: 'var(--font-body)' },
  body:        { padding: '24px', maxWidth: '700px', margin: '0 auto' },
  card:        { background: '#fff', padding: '20px', border: '1px solid #F1F1ED', marginBottom: '20px' },
  sectionTitle:{ margin: '0 0 8px', fontFamily: 'var(--font-headline)', color: '#333', fontWeight: 'normal' },
  hint:        { fontSize: '12px', color: '#aaa', marginBottom: '12px' },
  radioGroup:  { display: 'flex', gap: '24px', marginBottom: '16px' },
  radioLabel:  { fontSize: '14px', cursor: 'pointer', color: 'var(--brand-grey)', accentColor: 'var(--brand-purple)' },
  row:         { display: 'flex', gap: '16px', alignItems: 'flex-end', flexWrap: 'wrap' },
  field:       { flex: 1, minWidth: '140px', marginBottom: '14px' },
  label:       { display: 'block', fontSize: '13px', color: 'var(--brand-grey)', marginBottom: '4px' },
  select:      { width: '100%', height: '50px', padding: '0 10px', border: '1px solid #F1F1ED', fontSize: '14px', color: 'var(--brand-grey)' },
  fileInput:   { fontSize: '13px', color: 'var(--brand-grey)' },
  btn:         { padding: '10px 24px', background: 'var(--brand-purple)', color: '#fff', border: 'none', fontFamily: 'var(--font-highlight)', fontSize: '14px', cursor: 'pointer', whiteSpace: 'nowrap', borderRadius: '50px' },
  msgBox:      { marginTop: '12px', padding: '8px 12px', fontSize: '14px', borderRadius: '2px' },
  // [LARGE FILE FIX] Progress bar styles for large file upload tracking
  progressWrap:{ marginTop: '10px', background: '#f1f1ed', borderRadius: '4px', overflow: 'hidden', position: 'relative', height: '24px' },
  progressBar: { height: '100%', background: 'var(--brand-purple)', transition: 'width 0.3s ease' },
  progressLabel:{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', color: '#fff', fontFamily: 'var(--font-body)' },
};
